/**
 * SAYM: plays a product card's video while the mouse is over the card, and
 * stops and rewinds it when the mouse leaves. Horizon already switches the card
 * to its second medium on hover; this adds motion when that medium is a video.
 *
 * Delegated from the document, so cards added later (load more, saved products)
 * work too. Mouse only, like Horizon's own hover preview, and off entirely for
 * visitors who prefer reduced motion.
 */
const stillness = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @param {Event} event @returns {HTMLElement | null} */
function galleryFrom(event) {
  const target = event.target;
  return target instanceof Element ? target.closest('.card-gallery') : null;
}

/**
 * Liquid only exposes the 1080p rendition of a product video, which is far
 * heavier than a card needs. The storefront's product JSON lists every
 * rendition, so on the first hover the largest MP4 at or below 720p is swapped
 * in. Falls back to the markup's source if that lookup fails.
 * @param {HTMLElement} gallery
 * @param {HTMLVideoElement} video
 */
async function lighterSource(gallery, video) {
  if (video.dataset.sourceResolved) return;
  video.dataset.sourceResolved = 'pending';
  try {
    const link = gallery.closest('product-card')?.querySelector('a.product-card__link');
    const handle = link?.getAttribute('href')?.split('?')[0].split('/').filter(Boolean).pop();
    if (!handle) throw new Error('no handle');
    const root = window.Shopify?.routes?.root || '/';
    const product = await (await fetch(`${root}products/${handle}.js`)).json();
    const media = (product.media || []).find((m) => m.media_type === 'video');
    const best = (media?.sources || [])
      .filter((s) => s.format === 'mp4' && s.height <= 720)
      .sort((a, b) => b.height - a.height)[0];
    if (best && best.url !== video.currentSrc) {
      video.src = best.url;
      video.load();
    }
  } catch {
    // keep whatever source the markup provided
  }
  video.dataset.sourceResolved = 'done';
}

/** @param {HTMLElement} gallery */
function play(gallery) {
  const video = gallery.querySelector('video.saym-card-video');
  if (!(video instanceof HTMLVideoElement)) return;
  video.setAttribute('data-playing', '');
  lighterSource(gallery, video).finally(() => {
    // the pointer may have left during the lookup
    if (!video.hasAttribute('data-playing')) return;
    video.play().catch(() => video.removeAttribute('data-playing'));
  });
}

/** @param {HTMLElement} gallery */
function stop(gallery) {
  const video = gallery.querySelector('video.saym-card-video');
  if (!(video instanceof HTMLVideoElement)) return;
  video.pause();
  video.currentTime = 0;
  video.removeAttribute('data-playing');
}

document.addEventListener('pointerover', (event) => {
  if (event.pointerType !== 'mouse' || stillness.matches) return;
  const gallery = galleryFrom(event);
  if (!gallery) return;
  // Moving between children of the same card is not an entry
  if (event.relatedTarget instanceof Node && gallery.contains(event.relatedTarget)) return;
  play(gallery);
});

document.addEventListener('pointerout', (event) => {
  if (event.pointerType !== 'mouse') return;
  const gallery = galleryFrom(event);
  if (!gallery) return;
  if (event.relatedTarget instanceof Node && gallery.contains(event.relatedTarget)) return;
  stop(gallery);
});
